// The ledger's whole promise is "everything my bank knows about is here." But
// the Plaid bank-pull deliberately drops two kinds of synced line silently, both
// correct by design (ADR-072):
//   1. Untracked accounts — activity on Plaid accounts the treasurer didn't
//      select to track never produces a row.
//   2. Pre-onboarding history — lines dated on/before the backfill floor
//      (PriorFilingThroughDate) are history the committee filed OUTSIDE the app;
//      dropped silently, never even a straggler alert.
//
// A treasurer reconciling against their bank statement hits a missing line with
// no explanation, which reads as "the ledger is wrong" and destroys trust. This
// is the treasurer-first explanation so a missing line reads as EXPECTED, not
// broken. Engineer terms never render (feedback_treasurer_first_copy.md) — the
// copy translates at the FE render boundary, mirroring `sortInboxCopy` /
// `PreflightCopy`. String edits here are deliberate (pinned by
// `bankLineExclusionsCopy.test.ts`).

export const BankLineExclusionsCopy = {
  /** Section heading beneath the ledger. */
  Heading: 'Why a bank line might not be here',

  /** One-line intro — reassures the omission is deliberate, not a bug. */
  Intro:
    'Some bank activity is left out on purpose. If a line on your bank statement '
    + "isn't in your ledger, it's usually one of these — not a mistake:",

  /**
   * Untracked-account reason — always applies while a bank is connected: only the
   * accounts the treasurer selected sync in; other accounts at the same bank don't.
   */
  UntrackedAccount:
    "It's on an account you're not tracking. Only the accounts you picked when you "
    + 'linked the bank sync in — activity on your other accounts at that bank stays out.',
} as const;

/**
 * Pre-onboarding-history reason, carrying the concrete cutoff date (the mid-cycle
 * committee's prior-filing cutoff, already treasurer-formatted by the caller).
 * Shown only when that cutoff exists; a brand-new committee has no pre-onboarding
 * history to explain, so the caller omits this line entirely.
 */
export function preOnboardingHistoryReason(throughDateLabel: string): string {
  return (
    `It's dated on or before ${throughDateLabel}. Activity through that date was `
    + "already filed before you started here, so it isn't re-imported."
  );
}
